/**
 * Duty Roster Utility Functions
 * Centralized utility functions for duty roster management
 */

import { DutyRosterResidencyType } from '../types/enums';
import { DutyRosterGroup, GroupOffDays, HolidayOverride, DutyRosterAssignment, DutyRosterValidationResult } from '../types';
import { supabase } from '../integrations/supabase/client';

/**
 * Calculate working days for a given month using database RPC function
 * Ensures maximum 22 working days per month based on residency type
 */
export const calculateWorkingDays = async (
  year: number, 
  month: number, 
  residencyType: DutyRosterResidencyType
): Promise<number[]> => {
  // Validate input parameters
  if (year < 1900 || year > 2100) {
    throw new Error('Year must be between 1900 and 2100');
  }
  if (month < 1 || month > 12) {
    throw new Error('Month must be between 1 and 12');
  }
  if (!Object.values(DutyRosterResidencyType).includes(residencyType)) {
    throw new Error('Invalid residency type');
  }
  
  try {
    const { data, error } = await supabase.rpc('calculate_working_days_for_month', {
      p_year: year,
      p_month: month,
      p_residency_type: residencyType
    });
    
    if (error) {
      throw new Error(`Failed to calculate working days: ${error.message}`);
    }
    
    return data || [];
  } catch (error) {
    console.error('Error calculating working days:', error);
    throw error;
  }
};

/**
 * Calculate off days for a given month using database RPC function
 * Off days are calculated as the complement of working days
 */
export const calculateOffDays = async (
  year: number, 
  month: number, 
  residencyType: DutyRosterResidencyType
): Promise<number[]> => {
  // Validate input parameters
  if (year < 1900 || year > 2100) {
    throw new Error('Year must be between 1900 and 2100');
  }
  if (month < 1 || month > 12) {
    throw new Error('Month must be between 1 and 12');
  }
  if (!Object.values(DutyRosterResidencyType).includes(residencyType)) {
    throw new Error('Invalid residency type');
  }
  
  try {
    // Get working days first
    const workingDays = await calculateWorkingDays(year, month, residencyType);
    
    // Calculate off days as complement of working days
    const daysInMonth = new Date(year, month, 0).getDate();
    const offDays: number[] = [];
    
    for (let day = 1; day <= daysInMonth; day++) {
      if (!workingDays.includes(day)) {
        offDays.push(day);
      }
    }
    
    return offDays;
  } catch (error) {
    console.error('Error calculating off days:', error);
    throw error;
  }
};

/**
 * Generate month-year string in format "YYYY-MM"
 */
export const formatMonthYear = (year: number, month: number): string => {
  // Validate input parameters
  if (year < 1900 || year > 2100) {
    throw new Error('Year must be between 1900 and 2100');
  }
  if (month < 1 || month > 12) {
    throw new Error('Month must be between 1 and 12');
  }
  
  return `${year}-${month.toString().padStart(2, '0')}`;
};

/**
 * Parse month-year string to get year and month
 */
export const parseMonthYear = (monthYear: string): { year: number; month: number } => {
  // Validate input format
  if (!monthYear || typeof monthYear !== 'string') {
    throw new Error('Month-year must be a valid string');
  }
  
  const parts = monthYear.split('-');
  if (parts.length !== 2) {
    throw new Error('Month-year must be in format "YYYY-MM"');
  }
  
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  
  if (isNaN(year) || isNaN(month)) {
    throw new Error('Month-year must contain valid numbers');
  }
  
  if (year < 1900 || year > 2100) {
    throw new Error('Year must be between 1900 and 2100');
  }
  if (month < 1 || month > 12) {
    throw new Error('Month must be between 1 and 12');
  }
  
  return { year, month };
};

/**
 * Check if a specific date is a working day for a group
 * Updated to consider holiday overrides
 */
export const isWorkingDay = async (
  groupId: string,
  year: number,
  month: number,
  day: number
): Promise<boolean> => {
  try {
    const { data: workingDays, error } = await supabase.rpc('get_actual_working_days_for_group', {
      p_group_id: groupId,
      p_year: year,
      p_month: month
    });

    if (error) {
      throw new Error(`Failed to get working days: ${error.message}`);
    }

    return (workingDays || []).includes(day);
  } catch (error) {
    console.error('Error checking working day:', error);
    throw error;
  }
};

/**
 * Check if a specific date is an off day for a group
 * Updated to consider holiday overrides
 */
export const isOffDay = async (
  groupId: string,
  year: number,
  month: number,
  day: number
): Promise<boolean> => {
  try {
    const { data: offDays, error } = await supabase.rpc('get_actual_off_days_for_group', {
      p_group_id: groupId,
      p_year: year,
      p_month: month
    });

    if (error) {
      throw new Error(`Failed to get off days: ${error.message}`);
    }

    return (offDays || []).includes(day);
  } catch (error) {
    console.error('Error checking off day:', error);
    throw error;
  }
};

/**
 * Check if a date is a holiday
 */
export const isHoliday = (date: string, holidays: Array<{ date: string }>): boolean => {
  return holidays.some(holiday => holiday.date === date);
};

/**
 * Find next available working day for holiday collision resolution
 * Updated to work with async working day calculation
 * Now recursively searches into next month(s) if no working day found in current month
 */
export const findNextWorkingDay = async (
  groupId: string,
  startDate: string,
  holidays: Array<{ date: string }>,
  monthYear: string,
  maxSearchMonths: number = 6 // Prevent infinite loops, search up to 6 months ahead
): Promise<string> => {
  const { year, month } = parseMonthYear(monthYear);
  const daysInMonth = new Date(year, month, 0).getDate();
  
  let currentDay = new Date(startDate).getDate();
  
  // Search remaining days in current month
  while (currentDay <= daysInMonth) {
    currentDay++;
    const dateStr = `${year}-${month.toString().padStart(2, '0')}-${currentDay.toString().padStart(2, '0')}`;
    
    // Check if this day is a working day and not a holiday
    const isWorking = await isWorkingDay(groupId, year, month, currentDay);
    if (isWorking && !isHoliday(dateStr, holidays)) {
      return dateStr;
    }
  }
  
  // If no working day found in current month, search in next month
  if (maxSearchMonths > 0) {
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    const nextMonthYear = `${nextYear}-${nextMonth.toString().padStart(2, '0')}`;
    const firstDayOfNextMonth = `${nextYear}-${nextMonth.toString().padStart(2, '0')}-01`;
    
    // Recursively search in next month starting from day 0 (will be incremented to 1)
    const searchDate = new Date(nextYear, nextMonth - 1, 0); // Last day of previous month
    return findNextWorkingDay(groupId, searchDate.toISOString().split('T')[0], holidays, nextMonthYear, maxSearchMonths - 1);
  }
  
  // Fallback: if exhausted search (6 months ahead), return first day of next month with warning
  console.warn(`⚠️ No working day found within ${6 - maxSearchMonths} months ahead. Returning first day of next month as fallback.`);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  return `${nextYear}-${nextMonth.toString().padStart(2, '0')}-01`;
};

/**
 * Validate duty roster group data
 */
export const validateDutyRosterGroup = (group: Partial<DutyRosterGroup>, isUpdate: boolean = false): DutyRosterValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  if (!group.group_name?.trim()) {
    errors.push('Group name is required');
  }
  
  // Only validate company_id for creation, not updates
  if (!isUpdate && !group.company_id) {
    errors.push('Company ID is required');
  }
  
  if (group.residency_type && !Object.values(DutyRosterResidencyType).includes(group.residency_type as DutyRosterResidencyType)) {
    errors.push('Invalid residency type');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Validate group off days data
 */
export const validateGroupOffDays = (offDays: Partial<GroupOffDays>): DutyRosterValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  if (!offDays.month_year) {
    errors.push('Month-year is required');
  }
  
  if (!offDays.group_id) {
    errors.push('Group ID is required');
  }
  
  if (!offDays.off_days || !Array.isArray(offDays.off_days)) {
    errors.push('Off days array is required');
  } else {
    const { year, month } = parseMonthYear(offDays.month_year);
    const daysInMonth = new Date(year, month, 0).getDate();
    
    // Validate off days are within month range
    const invalidDays = offDays.off_days.filter(day => day < 1 || day > daysInMonth);
    if (invalidDays.length > 0) {
      errors.push(`Invalid off days: ${invalidDays.join(', ')}`);
    }
    
    // Check for duplicate off days
    const uniqueDays = new Set(offDays.off_days);
    if (uniqueDays.size !== offDays.off_days.length) {
      errors.push('Duplicate off days found');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Validate holiday override data
 */
export const validateHolidayOverride = (override: Partial<HolidayOverride>): DutyRosterValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  if (!override.group_id) {
    errors.push('Group ID is required');
  }
  
  if (!override.org_off_day) {
    errors.push('Original off day is required');
  }
  
  if (!override.off_day_shifted_to) {
    errors.push('Shifted off day is required');
  }
  
  if (!override.month_year) {
    errors.push('Month-year is required');
  }
  
  // Validate date format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (override.org_off_day && !dateRegex.test(override.org_off_day)) {
    errors.push('Invalid original off day format');
  }
  
  if (override.off_day_shifted_to && !dateRegex.test(override.off_day_shifted_to)) {
    errors.push('Invalid shifted off day format');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Generate duty roster assignments for a group
 */
export const generateDutyRosterAssignments = (
  groupId: string,
  monthYear: string,
  workingDays: number[],
  offDays: number[],
  shiftId: string
): DutyRosterAssignment[] => {
  const { year, month } = parseMonthYear(monthYear);
  const assignments: DutyRosterAssignment[] = [];
  
  // Generate working day assignments
  workingDays.forEach(day => {
    const date = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    assignments.push({
      employee_id: '', // Will be filled when assigning to specific employees
      group_id: groupId,
      date,
      shift_id: shiftId,
      is_working_day: true,
      is_off_day: false
    });
  });
  
  // Generate off day assignments
  offDays.forEach(day => {
    const date = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    assignments.push({
      employee_id: '', // Will be filled when assigning to specific employees
      group_id: groupId,
      date,
      shift_id: '', // No shift for off days
      is_working_day: false,
      is_off_day: true
    });
  });
  
  return assignments;
};

/**
 * Check if employee can work overtime on a specific date
 */
export const canWorkOvertime = (
  employeeId: string,
  date: string,
  assignments: DutyRosterAssignment[],
  shifts: Array<{ id: string; in_working_hours: boolean }>
): boolean => {
  const assignment = assignments.find(a => a.employee_id === employeeId && a.date === date);
  
  if (!assignment) {
    return false; // No assignment found
  }
  
  if (assignment.is_off_day) {
    return true; // Can work OT on off days
  }
  
  if (assignment.is_working_day) {
    const shift = shifts.find(s => s.id === assignment.shift_id);
    // Can only work OT in shifts where in_working_hours = false
    return shift ? !shift.in_working_hours : false;
  }
  
  return false;
};

/**
 * Get employee's shift for a specific date
 */
export const getEmployeeShiftForDate = (
  employeeId: string,
  date: string,
  assignments: DutyRosterAssignment[]
): string | null => {
  const assignment = assignments.find(a => a.employee_id === employeeId && a.date === date);
  return assignment?.shift_id || null;
};

/**
 * Detect holiday collisions with duty roster off days
 * Returns collision status and next available working day
 */
export const detectHolidayCollision = async (
  groupId: string,
  date: string,
  companyId: string
): Promise<{
  hasCollision: boolean;
  isOffDay: boolean;
  isHoliday: boolean;
  holidayId?: string;
  holidayName?: string;
  originalDate?: string;
  shiftedDate?: string;
  message: string;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase.rpc('detect_holiday_collisions', {
      p_group_id: groupId,
      p_date: date,
      p_company_id: companyId
    });
    
    if (error) {
      throw new Error(`Failed to detect holiday collision: ${error.message}`);
    }
    
    return {
      hasCollision: data.has_collision || false,
      isOffDay: data.is_off_day || false,
      isHoliday: data.is_holiday || false,
      holidayId: data.holiday_id,
      holidayName: data.holiday_name,
      originalDate: data.original_date,
      shiftedDate: data.shifted_date,
      message: data.message || 'No collision detected',
      error: data.error
    };
  } catch (error) {
    console.error('Error detecting holiday collision:', error);
    throw error;
  }
};

/**
 * Handle holiday collision resolution (R1.md Scenario 5)
 * This is the main function called during OT validation
 */
export const handleHolidayCollision = async (
  groupId: string,
  date: string,
  companyId: string
): Promise<string | null> => {
  try {
    const collisionResult = await detectHolidayCollision(groupId, date, companyId);
    
    if (collisionResult.hasCollision && collisionResult.shiftedDate) {
      return collisionResult.shiftedDate;
    }
    
    return null;
  } catch (error) {
    console.error('Error handling holiday collision:', error);
    throw error;
  }
};

// NEW: Employee Management Utilities

/**
 * Validate employee-group residency compatibility
 */
export const validateEmployeeGroupResidency = async (
  employeeId: string,
  groupId: string
): Promise<{
  isValid: boolean;
  employeeResidency: string;
  groupResidency: string;
  employeeName: string;
  groupName: string;
  message: string;
}> => {
  try {
    const { data, error } = await supabase.rpc('validate_employee_group_residency', {
      p_employee_id: employeeId,
      p_group_id: groupId
    });
    
    if (error) {
      throw new Error(`Failed to validate residency: ${error.message}`);
    }
    
    return {
      isValid: data.is_valid || false,
      employeeResidency: data.employee_residency || '',
      groupResidency: data.group_residency || '',
      employeeName: data.employee_name || '',
      groupName: data.group_name || '',
      message: data.message || 'Validation failed'
    };
  } catch (error) {
    console.error('Error validating employee-group residency:', error);
    throw error;
  }
};

/**
 * Assign employee to duty roster group
 */
export const assignEmployeeToGroup = async (
  employeeId: string,
  groupId: string,
  assignedBy?: string
): Promise<{
  success: boolean;
  employeeId: string;
  employeeName: string;
  groupId: string;
  groupName: string;
  message: string;
}> => {
  try {
    const { data, error } = await supabase.rpc('assign_employee_to_group', {
      p_employee_id: employeeId,
      p_group_id: groupId,
      p_assigned_by: assignedBy || null
    });
    
    if (error) {
      throw new Error(`Failed to assign employee to group: ${error.message}`);
    }
    
    return {
      success: data.success || false,
      employeeId: data.employee_id || employeeId,
      employeeName: data.employee_name || '',
      groupId: data.group_id || groupId,
      groupName: data.group_name || '',
      message: data.message || 'Assignment failed'
    };
  } catch (error) {
    console.error('Error assigning employee to group:', error);
    throw error;
  }
};

/**
 * Bulk assign multiple employees to a group
 */
export const bulkAssignEmployeesToGroup = async (
  employeeIds: string[],
  groupId: string,
  assignedBy?: string
): Promise<{
  totalProcessed: number;
  successfulAssignments: number;
  failedAssignments: number;
  successfulDetails: any[];
  failedDetails: Array<{ employeeId: string; error: string }>;
  message: string;
}> => {
  try {
    const { data, error } = await supabase.rpc('bulk_assign_employees_to_group', {
      p_employee_ids: employeeIds,
      p_group_id: groupId,
      p_assigned_by: assignedBy || null
    });
    
    if (error) {
      throw new Error(`Failed to bulk assign employees: ${error.message}`);
    }
    
    return {
      totalProcessed: data.total_processed || 0,
      successfulAssignments: data.successful_assignments || 0,
      failedAssignments: data.failed_assignments || 0,
      successfulDetails: data.successful_details || [],
      failedDetails: data.failed_details || [],
      message: data.message || 'Bulk assignment failed'
    };
  } catch (error) {
    console.error('Error bulk assigning employees:', error);
    throw error;
  }
};

/**
 * Remove employee from duty roster group
 */
export const removeEmployeeFromGroup = async (
  employeeId: string,
  removedBy?: string
): Promise<{
  success: boolean;
  employeeId: string;
  employeeName: string;
  removedGroupId?: string;
  removedGroupName?: string;
  message: string;
}> => {
  try {
    const { data, error } = await supabase.rpc('remove_employee_from_group', {
      p_employee_id: employeeId,
      p_removed_by: removedBy || null
    });
    
    if (error) {
      throw new Error(`Failed to remove employee from group: ${error.message}`);
    }
    
    return {
      success: data.success || false,
      employeeId: data.employee_id || employeeId,
      employeeName: data.employee_name || '',
      removedGroupId: data.removed_group_id,
      removedGroupName: data.removed_group_name,
      message: data.message || 'Removal failed'
    };
  } catch (error) {
    console.error('Error removing employee from group:', error);
    throw error;
  }
};

/**
 * Get employees by duty roster status
 */
export const getEmployeesByDutyRosterStatus = async (
  companyId: string,
  isDutyRoster?: boolean,
  includeInactive: boolean = false
): Promise<{
  employees: any[];
  summary: {
    totalCount: number;
    dutyRosterCount: number;
    nonDutyRosterCount: number;
    filterApplied: boolean;
    includeInactive: boolean;
  };
}> => {
  try {
    const { data, error } = await supabase.rpc('get_employees_by_duty_roster_status', {
      p_company_id: companyId,
      p_is_duty_roster: isDutyRoster || null,
      p_include_inactive: includeInactive
    });
    
    if (error) {
      throw new Error(`Failed to get employees by duty roster status: ${error.message}`);
    }
    
    return {
      employees: data.employees || [],
      summary: data.summary || {
        totalCount: 0,
        dutyRosterCount: 0,
        nonDutyRosterCount: 0,
        filterApplied: false,
        includeInactive: false
      }
    };
  } catch (error) {
    console.error('Error getting employees by duty roster status:', error);
    throw error;
  }
};

/**
 * Get duty roster employees with their group information
 */
export const getDutyRosterEmployeesWithGroups = async (
  companyId: string,
  groupId?: string
): Promise<{
  employees: any[];
  groups: any[];
  summary: {
    totalEmployees: number;
    groupedEmployees: number;
    ungroupedEmployees: number;
    totalGroups: number;
  };
}> => {
  try {
    const { data, error } = await supabase.rpc('get_duty_roster_employees_with_groups', {
      p_company_id: companyId,
      p_group_id: groupId || null
    });
    
    if (error) {
      throw new Error(`Failed to get duty roster employees with groups: ${error.message}`);
    }
    
    return {
      employees: data.employees || [],
      groups: data.groups || [],
      summary: data.summary || {
        totalEmployees: 0,
        groupedEmployees: 0,
        ungroupedEmployees: 0,
        totalGroups: 0
      }
    };
  } catch (error) {
    console.error('Error getting duty roster employees with groups:', error);
    throw error;
  }
};

/**
 * Validate employee duty roster assignment
 */
export const validateEmployeeDutyRosterAssignment = async (
  employeeId: string
): Promise<{
  isValid: boolean;
  employeeId: string;
  employeeName: string;
  isDutyRoster: boolean;
  groupId?: string;
  groupName?: string;
  validationErrors: string[];
  warnings: string[];
  message: string;
}> => {
  try {
    const { data, error } = await supabase.rpc('validate_employee_duty_roster_assignment', {
      p_employee_id: employeeId
    });
    
    if (error) {
      throw new Error(`Failed to validate employee duty roster assignment: ${error.message}`);
    }
    
    return {
      isValid: data.is_valid || false,
      employeeId: data.employee_id || employeeId,
      employeeName: data.employee_name || '',
      isDutyRoster: data.is_duty_roster || false,
      groupId: data.group_id,
      groupName: data.group_name,
      validationErrors: data.validation_errors || [],
      warnings: data.warnings || [],
      message: data.message || 'Validation failed'
    };
  } catch (error) {
    console.error('Error validating employee duty roster assignment:', error);
    throw error;
  }
};

/**
 * Check if employee is duty roster employee
 */
export const isDutyRosterEmployee = (employee: any): boolean => {
  return employee?.is_duty_roster === true;
};

/**
 * Check if employee is assigned to a group
 */
export const isEmployeeAssignedToGroup = (employee: any): boolean => {
  return employee?.is_duty_roster === true && !!employee?.group_id;
};

/**
 * Get employee's group information
 */
export const getEmployeeGroupInfo = (employee: any): {
  groupId?: string;
  groupName?: string;
  groupResidencyType?: string;
} => {
  return {
    groupId: employee?.group_id,
    groupName: employee?.group_name,
    groupResidencyType: employee?.group_residency_type
  };
};

/**
 * Filter employees by duty roster status
 */
export const filterEmployeesByDutyRosterStatus = (
  employees: any[],
  isDutyRoster?: boolean
): any[] => {
  if (isDutyRoster === undefined) {
    return employees;
  }
  
  return employees.filter(emp => emp.is_duty_roster === isDutyRoster);
};

/**
 * Filter employees by group
 */
export const filterEmployeesByGroup = (
  employees: any[],
  groupId?: string
): any[] => {
  if (!groupId) {
    return employees;
  }
  
  return employees.filter(emp => emp.group_id === groupId);
};

/**
 * Get employees without group assignment
 */
export const getUngroupedDutyRosterEmployees = (employees: any[]): any[] => {
  return employees.filter(emp => emp.is_duty_roster === true && !emp.group_id);
};
